<template>
    <div v-if="error" id="content" class="wrap w-100">
        <div class="grid">
            <div class="unit whole align-center">
                <v-img class="home-logo" :src="`/common/img/giglets-logo-grey.png`" alt="Giglets" />
            </div>
        </div>
        <div class="grid">
            <div class="unit half offset-one-quarter">
                <v-card class="mx-auto mt-5 px-12 py-8" elevation="2" max-width="448" rounded="lg">
                    <h2 class="mb-5">Please sign in</h2>

                    <v-alert variant="outlined" :text="error" type="error"></v-alert>

                    <div class="mt-6 d-flex flex-column align-center">
                        <v-btn @click="signin" block variant="flat" color="brand" size="large" type="submit">
                            Sign in
                        </v-btn>
                        <a
                            class="text-subtitle-1 text-decoration-none text-blue mt-3"
                            target="_blank"
                            href="https://stonly.com/guide/en/having-trouble-logging-in-mukrjHZ0xp/Steps/3183749"
                            >Having trouble logging in?</a
                        >
                    </div>
                </v-card>
                <div class="d-flex justify-space-between px-3 mt-3">
                    <a class="text-caption text-decoration-none text-blue" href="https://www.giglets.com/Legal"
                        >End User Licence Agreement</a
                    >
                    <a class="text-caption text-decoration-none text-blue" href="https://www.giglets.com/Legal"
                        >Access Licence Agreement</a
                    >
                </div>
            </div>
        </div>
    </div>
    <v-overlay :model-value="signInInProgress" class="align-center justify-center">
        <v-progress-circular indeterminate color="brand"></v-progress-circular>
    </v-overlay>
</template>

<script setup lang="ts">
import { UserType } from '@/stores/auth';
import axios from 'axios';

definePage({
    meta: {
        noAuth: true,
        layout: 'bare',
    },
});

const auth = useAuth();
const { subscriptions, isLoggedIn, userType } = storeToRefs(auth);
const route = useRoute();
const router = useRouter();
const signInInProgress = ref(false);

const basePath = (process.env.VITE_BASE_PATH || '').replace(/\/$/, '');

const error = ref<string | undefined>();

if (isLoggedIn.value && userType.value) {
    gotoHomePage();
} else if (typeof route.query.code === 'string') {
    signInInProgress.value = true;
    // No await here since that delays showing the UI
    auth.loginWithCode(route.query.code, window.location.origin + basePath + '/landing')
        .then(() => {
            const hasGigletsSubscription = subscriptions.value?.some((subscription) => subscription.type === 'giglets');
            if (!hasGigletsSubscription) {
                throw new Error('No Giglets subscription found');
            }
        })
        .then(() => {
            return axios.get<{ type: 'pupil' | 'teacher' }>('/api/user-type');
        })
        .then((res) => {
            userType.value = res.data.type;
            gotoHomePage();
        })
        .catch((err) => {
            console.log(err);
            if (err.message === 'No Giglets subscription found') {
                error.value = 'You do not have access to Giglets. Please contact your school administrator.';
            } else {
                displayLoginError();
            }
            auth.logout();
        })
        .finally(() => {
            signInInProgress.value = false;
        });
} else if (typeof route.query.token === 'string' && typeof route.query.type === 'string') {
    login(route.query.type as UserType, route.query.token);
} else if (route.query.reauthenticate !== undefined) {
    redirectToIltAuthLogin(route.query.reauthenticate ? route.query.reauthenticate.toString() : 'auto');
} else {
    redirectToIltAuthLogin();
}

async function signin() {
    signInInProgress.value = true;
    redirectToIltAuthLogin();
}

async function gotoHomePage() {
    if (userType.value === 'pupil') {
        await router.push('/pupil/change-book');
    } else if (userType.value === 'teacher') {
        await router.push('/teacher/home');
    } else {
        displayLoginError();
    }
}

function login(type: UserType, token?: string) {
    return auth
        .loginWithToken(type, token)
        .then(() => {
            userType.value = type;
            let path = '';
            if (type === 'pupil') {
                path = '/pupil/change-book';
            } else if (type === 'teacher') {
                path = '/teacher/home';
            } else if (type === 'admin') {
                path = '/admin/home';
            }
            return router.push(path);
        })
        .catch((err) => {
            console.log(err);
            displayLoginError();
        });
}

function displayLoginError() {
    error.value = 'Sorry, we did not recognise your username and password, please try again.';
}

function redirectToIltAuthLogin(reauthenticate?: string) {
    window.location.href = auth.createIltAuthUrl(window.location.origin + basePath + '/landing', reauthenticate);
}
</script>
<style scoped lang="scss">
.text-subtitle-1 {
    font-family: 'HeinemannSpecial-Roman', sans-serif !important;
}
.home-logo {
    width: 250px;
    margin: 20px auto 0 auto;
}
</style>
